/**
 * Consumer Portal Header
 *
 * Lean AppNavBar — page title/description/actions are now rendered in-content
 * by PageContentHeader inside PageTemplate.
 *
 * @see Issue #699 - Notification System Fidelity (NotificationBell + Dropdown)
 * @see PR #856 — Navigation & Layout Redesign
 */

import {
  AppNavBar,
  Box,
  baseColors,
  GlassIconButton,
  GlassTooltip,
  NotificationBell,
  NotificationDropdown,
  type NotificationItemData,
  useDesignSystem,
  useI18n,
  useSidebarWidth,
} from '@ops-matrix/design-system-ui';
import { isSafeActionUrl } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useMarkAllRead, useMarkAsRead, useMarkAsUnread, useUnreadCount } from '../../hooks/useNotifications';
import { useConsumerNotificationsList } from '../../lib/trpc/hooks';
import { SearchButton } from '../CommandPalette';

// Perf: defer react-markdown + the chat UI until the user actually opens Astor.
const AstorChatFlyover = lazy(() => import('../astor/AstorChatFlyover').then((m) => ({ default: m.AstorChatFlyover })));

interface HeaderProps {
  showMenuButton: boolean;
  onMenuClick: () => void;
  portalName?: string;
  onLogoClick?: () => void;
  hideUserAvatar?: boolean;
}

const NOTIF_QUERY_INPUT = { page: 1, limit: 5 } as const;

export const Header = ({
  showMenuButton,
  onMenuClick,
  portalName,
  onLogoClick,
  hideUserAvatar,
}: HeaderProps): ReactElement => {
  const navigate = useNavigate();
  const { consumer, logout } = useConsumerAuth();
  const { mode, toggleMode } = useDesignSystem();
  const { t } = useI18n();
  const { sidebarWidth } = useSidebarWidth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Notification data (Issue #699)
  const { data: unreadCountData } = useUnreadCount();
  const unreadCount = unreadCountData?.count ?? 0;

  const { data: notifData, isLoading: notifLoading } = useConsumerNotificationsList(NOTIF_QUERY_INPUT);
  const markAsReadMutation = useMarkAsRead();
  const markAsUnreadMutation = useMarkAsUnread();
  const markAllReadMutation = useMarkAllRead();

  const notificationItems: NotificationItemData[] = useMemo(() => notifData?.data ?? [], [notifData]);

  const handleBellClick = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const handleViewAll = useCallback(() => {
    setDropdownOpen(false);
    navigate('/notifications');
  }, [navigate]);

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  const handleNotificationClick = useCallback(
    (notification: NotificationItemData) => {
      if (!notification.isRead) {
        markAsReadMutation.mutate({ notificationId: notification.id });
      }
      const url = notification.actionUrl;
      setDropdownOpen(false);
      if (url) {
        // Audit §3.1 — never follow an untrusted actionUrl. Defense-in-depth
        // for legacy rows predating service-layer validation.
        const target = isSafeActionUrl(url) ? url : '/notifications';
        if (target !== url) {
          // Deliberately NOT logging notification.id or the url — both would
          // leak sensitive data into devtools.
          // biome-ignore lint/suspicious/noConsole: surface in browser telemetry
          console.warn('[notification] blocked unsafe actionUrl');
        }
        setTimeout(() => navigate(target), 0);
      }
    },
    [markAsReadMutation, navigate],
  );

  const handleToggleRead = useCallback(
    (notification: NotificationItemData) => {
      if (notification.isRead) {
        markAsUnreadMutation.mutate({ notificationId: notification.id });
      } else {
        markAsReadMutation.mutate({ notificationId: notification.id });
      }
    },
    [markAsReadMutation, markAsUnreadMutation],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  // Astor chat button with wave gradient animation (matching business portal)
  const astorChatAction = (
    <GlassTooltip title={t('astorChat.tooltip')}>
      <GlassIconButton
        size="small"
        onClick={() => setChatOpen(true)}
        sx={{
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            // Borrower portal Astor sparkle — mint gradient (matches Fiops Borrower Portal
            // redesign tokens.css `--f-mint`). Light mode uses the deeper teal[700] anchor;
            // dark mode shifts to a brighter teal[400] center to keep contrast on dark chrome.
            // T1: hex literals replaced with `baseColors.teal[*]` theme tokens.
            background:
              mode === 'dark'
                ? `linear-gradient(45deg, ${baseColors.teal[600]}, ${baseColors.teal[400]}, ${baseColors.teal[200]}, ${baseColors.teal[400]}, ${baseColors.teal[600]})`
                : `linear-gradient(45deg, ${baseColors.teal[700]}, ${baseColors.teal[600]}, ${baseColors.teal[300]}, ${baseColors.teal[600]}, ${baseColors.teal[700]})`,
            backgroundSize: '200% 200%',
            animation: 'astorIconShimmer 6s linear infinite',
            mask: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25zm0 6l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25zm-7.5-5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12z'/%3E%3C/svg%3E\") center/contain no-repeat",
            WebkitMask:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25zm0 6l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25zm-7.5-5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12z'/%3E%3C/svg%3E\") center/contain no-repeat",
            '@keyframes astorIconShimmer': {
              '0%': {
                backgroundPosition: '0% 0%',
              },
              '100%': {
                backgroundPosition: '200% 200%',
              },
            },
          }}
        />
      </GlassIconButton>
    </GlassTooltip>
  );

  // Combined header actions: Astor chat + notification bell
  const headerActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {astorChatAction}
      <NotificationBell
        ref={bellRef}
        unreadCount={unreadCount}
        onClick={handleBellClick}
        isOpen={dropdownOpen}
        size="small"
      />
    </Box>
  );

  return (
    <>
      <AppNavBar
        // Branding
        portalName={portalName}
        onLogoClick={onLogoClick}
        // Navigation
        showMenuButton={showMenuButton}
        onMenuClick={onMenuClick}
        // Search
        showSearch
        searchComponent={<SearchButton />}
        // Theme - simple toggle (dark/light)
        showThemeToggle
        themeMode={mode}
        simpleThemeToggle
        onThemeToggle={toggleMode}
        // User
        user={consumer ? { name: consumer.name, email: consumer.email } : undefined}
        onLogout={handleLogout}
        onProfile={() => navigate('/profile')}
        onSettings={() => navigate('/notifications/preferences')}
        // T2: surface the most-used profile sub-pages directly in the dropdown
        // so users don't have to dig through generic Settings.
        onNotificationPreferences={() => navigate('/notifications/preferences')}
        onAstorPreferences={() => navigate('/profile?tab=astor')}
        // Layout - Fixed full-width header
        position="fixed"
        sidebarWidth={sidebarWidth}
        elevation={1}
        hideUserAvatar={hideUserAvatar}
        // Actions: Astor chat + notification bell
        actions={headerActions}
      />

      {/* Astor Chat Flyover — lazy so markdown + chat UI stay out of the baseline */}
      {chatOpen && (
        <Suspense fallback={null}>
          <AstorChatFlyover open={chatOpen} onClose={() => setChatOpen(false)} />
        </Suspense>
      )}

      {/* Notification Dropdown (Issue #699) */}
      <NotificationDropdown
        open={dropdownOpen}
        anchorEl={bellRef.current}
        onClose={handleDropdownClose}
        notifications={notificationItems}
        loading={notifLoading}
        onViewAll={handleViewAll}
        onMarkAllRead={handleMarkAllRead}
        onNotificationClick={handleNotificationClick}
        onToggleRead={handleToggleRead}
      />
    </>
  );
};
